<template>
  <div class="manage-repair-slip-transfer main row">
    <div class="col-md-12">
      <KTCodePreview v-bind:title="'Duyệt phiếu luân chuyển'">
        <template v-slot:preview>
          <b-tabs v-model="activeTab" @input="onTabChange">
            <b-tab title="Chờ tôi duyệt nhận" />
            <b-tab title="Tôi gửi đi" />
            <b-tab title="Toàn hệ thống" />
          </b-tabs>

          <b-table
            class="mt-3 table-vertical-center"
            :items="rows"
            :fields="fields"
            :busy="loading"
            show-empty
            empty-text="Không có lệnh luân chuyển nào"
            bordered
            small
            responsive
          >
            <template #table-busy>
              <div class="text-center text-primary my-3">
                <b-spinner class="align-middle"></b-spinner>
                <strong class="ml-2">Đang tải...</strong>
              </div>
            </template>

            <template #cell(slip)="{ item }">
              <a
                href="javascript:void(0)"
                class="font-weight-bold"
                @click="goToSlip(item.repairSlipId)"
              >
                {{ (item.repairSlip && item.repairSlip.slipNumber) || item.repairSlipId }}
              </a>
              <div v-if="item.repairSlip" class="small text-muted">
                {{ item.repairSlip.customerName }}
                <span v-if="item.repairSlip.imei"> — IMEI {{ item.repairSlip.imei }}</span>
              </div>
            </template>

            <template #cell(route)="{ item }">
              {{ (item.fromStore && item.fromStore.name) || ('CH #' + item.fromStoreId) }}
              <i class="fas fa-arrow-right mx-1 text-muted"></i>
              {{ (item.toStore && item.toStore.name) || ('CH #' + item.toStoreId) }}
              <b-badge
                :variant="item.direction === 'return' ? 'info' : 'secondary'"
                class="ml-1"
              >
                {{ item.direction === 'return' ? 'Trả về' : 'Đi sửa' }}
              </b-badge>
            </template>

            <template #cell(shipped)="{ item }">
              <div>{{ formatDateTime(item.shippedAt) }}</div>
              <div v-if="item.shippedBy && item.shippedBy.employee" class="small text-muted">
                {{ item.shippedBy.employee.fullName }}
              </div>
              <div v-if="item.shippingNote" class="small">{{ item.shippingNote }}</div>
            </template>

            <template #cell(received)="{ item }">
              <div v-if="item.receivedAt">{{ formatDateTime(item.receivedAt) }}</div>
              <div v-if="item.receivedBy && item.receivedBy.employee" class="small text-muted">
                {{ item.receivedBy.employee.fullName }}
              </div>
              <div v-if="item.receivingNote" class="small">{{ item.receivingNote }}</div>
            </template>

            <template #cell(transferStatus)="{ item }">
              <b-badge :variant="statusMeta(item.transferStatus).variant">
                {{ statusMeta(item.transferStatus).text }}
              </b-badge>
            </template>

            <template #cell(actions)="{ item }">
              <b-button
                v-if="activeTab === 0 && isShipping(item)"
                variant="warning"
                size="sm"
                @click="openApprove(item)"
              >
                <i class="fas fa-check mr-1"></i>Duyệt nhận
              </b-button>
              <b-button
                v-else-if="activeTab === 1 && isShipping(item)"
                variant="outline-danger"
                size="sm"
                @click="onCancel(item)"
              >
                <i class="fas fa-times mr-1"></i>Huỷ lệnh
              </b-button>
              <span v-else class="text-muted">—</span>
            </template>
          </b-table>

          <b-row class="mt-3 align-items-center">
            <b-col md="6">
              <p class="text-dark mb-0">Tổng: {{ totalRow }} lệnh</p>
            </b-col>
            <b-col md="6">
              <b-pagination
                v-show="totalPage >= 2"
                v-model="page"
                :total-rows="totalRow"
                :per-page="limit"
                align="right"
                class="mb-0"
                @change="onPageChange"
              ></b-pagination>
            </b-col>
          </b-row>
        </template>
      </KTCodePreview>
    </div>

    <!-- Modal duyệt nhận máy -->
    <b-modal id="modal-approve-transfer" title="Duyệt nhận máy luân chuyển" hide-footer lazy>
      <b-alert v-if="approveModal.error" variant="danger" show class="py-2">
        {{ approveModal.error }}
      </b-alert>
      <p v-if="approveModal.item" class="mb-2">
        Phiếu
        <strong>
          {{ (approveModal.item.repairSlip && approveModal.item.repairSlip.slipNumber)
            || approveModal.item.repairSlipId }}
        </strong>
        từ
        <strong>
          {{ (approveModal.item.fromStore && approveModal.item.fromStore.name)
            || ('CH #' + approveModal.item.fromStoreId) }}
        </strong>
      </p>
      <b-form-group label="Ghi chú tình trạng máy lúc nhận">
        <b-form-textarea
          v-model="approveModal.receivingNote"
          rows="3"
          placeholder="VD: Đã nhận máy vật lý, ngoại hình nguyên vẹn, máy lên nguồn bình thường..."
        ></b-form-textarea>
      </b-form-group>
      <div class="d-flex justify-content-end mt-3">
        <b-button
          variant="secondary"
          size="sm"
          class="mr-2"
          @click="$bvModal.hide('modal-approve-transfer')"
        >
          Huỷ
        </b-button>
        <b-button
          variant="warning"
          size="sm"
          :disabled="approveModal.submitting"
          @click="submitApprove"
        >
          <i class="fas fa-check mr-1"></i>
          {{ approveModal.submitting ? 'Đang xử lý...' : 'Xác nhận nhận máy' }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import ApiService from '@/core/services/api.service';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { TRANSFER_STATUS, TRANSFER_STATUS_META } from '@/utils/warranty-service.constants';
import { extractError, formatDateTime } from '@/utils/warranty-service.helpers';

const TRANSFER_API = 'v2/warranty-service/repair-slip-transfers';
const TABS = ['incoming', 'outgoing', 'system'];

const FIELDS = Object.freeze([
  { key: 'slip', label: 'Phiếu sửa chữa' },
  { key: 'route', label: 'Tuyến luân chuyển' },
  { key: 'shipped', label: 'Gửi đi' },
  { key: 'received', label: 'Duyệt nhận' },
  { key: 'transferStatus', label: 'Trạng thái', tdClass: 'text-center' },
  { key: 'actions', label: 'Thao tác', tdClass: 'text-center' },
]);

export default {
  name: 'ManageRepairSlipTransfer',
  components: { KTCodePreview },
  data() {
    return {
      activeTab: 0,
      rows: [],
      loading: false,
      page: 1,
      limit: 20,
      totalRow: 0,
      totalPage: 0,
      approveModal: {
        item: null,
        receivingNote: '',
        submitting: false,
        error: '',
      },
    };
  },
  computed: {
    fields: () => FIELDS,
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Dịch vụ bảo hành' },
      { title: 'Duyệt phiếu luân chuyển' },
    ]);
    this.fetchData();
  },
  methods: {
    formatDateTime,
    statusMeta(status) {
      return TRANSFER_STATUS_META[Number(status)] || { text: status, variant: 'secondary' };
    },
    isShipping(item) {
      return Number(item.transferStatus) === TRANSFER_STATUS.SHIPPING;
    },
    onTabChange() {
      this.page = 1;
      this.fetchData();
    },
    onPageChange(newPage) {
      this.page = newPage;
      this.$nextTick(() => this.fetchData());
    },
    async fetchData() {
      this.loading = true;
      try {
        const { data } = await ApiService.query(TRANSFER_API, {
          params: {
            tab: TABS[this.activeTab],
            page: this.page,
            limit: this.limit,
          },
        });
        const payload = (data && data.data) || {};
        this.rows = payload.rows || [];
        this.totalRow = payload.count || 0;
        this.totalPage = Math.ceil(this.totalRow / this.limit);
      } catch (err) {
        makeToastFaile(extractError(err));
        this.rows = [];
        this.totalRow = 0;
      } finally {
        this.loading = false;
      }
    },
    goToSlip(id) {
      const route = this.$router.resolve({ name: 'edit-repair-slip', params: { id } });
      window.open(route.href, '_blank');
    },
    openApprove(item) {
      this.approveModal = {
        item,
        receivingNote: '',
        submitting: false,
        error: '',
      };
      this.$bvModal.show('modal-approve-transfer');
    },
    async submitApprove() {
      this.approveModal.submitting = true;
      this.approveModal.error = '';
      try {
        await ApiService.put(`${TRANSFER_API}/${this.approveModal.item.id}/approve`, {
          receivingNote: String(this.approveModal.receivingNote || '').trim() || null,
        });
        makeToastSuccess('Đã duyệt nhận máy.');
        this.$bvModal.hide('modal-approve-transfer');
        this.fetchData();
      } catch (err) {
        this.approveModal.error = extractError(err);
        makeToastFaile(this.approveModal.error);
      } finally {
        this.approveModal.submitting = false;
      }
    },
    async onCancel(item) {
      const ok = await this.$bvModal.msgBoxConfirm(
        'Huỷ lệnh luân chuyển này? Máy sẽ vẫn ở cửa hàng hiện tại.',
        {
          title: 'Xác nhận huỷ lệnh',
          okVariant: 'danger',
          okTitle: 'Huỷ lệnh',
          cancelTitle: 'Đóng',
          centered: true,
        },
      );
      if (!ok) return;
      try {
        await ApiService.put(`${TRANSFER_API}/${item.id}/cancel`, {});
        makeToastSuccess('Đã huỷ lệnh luân chuyển.');
        this.fetchData();
      } catch (err) {
        makeToastFaile(extractError(err));
      }
    },
  },
};
</script>
